interface SeoData {
    title?: string | null
    description?: string | null
    author?: string | null
    image?: string | null
    url?: string | null
    enableTitleSuffix?: boolean
    imageMeta?: any
    published_time?: string | null
    modified_time?: string | null
    articleBody?: string | null
    section?: string | null
    tags?: string[] | null
    twitter_username?: string | null
    schema?: any
    type?: string
    site_name?: string
    favicon?: string | null
    locale?: string
    robots?: string | null
    canonical_url?: string | null
    openGraphTitle?: string | null
    alternates?: any
}

const defaultSiteName = 'swim4you.ru'

export const useSeoData = (seoData: SeoData | null | undefined, currentPath?: string): any => {
    if (!seoData) {
        return {}
    }

    // Формируем title
    let title = seoData.title || ''

    // Формируем описание
    const description = seoData.description || ''

    // Формируем URL
    let url = seoData.url
    if (!url) {
        if (process.client) {
            url = window.location.href
        } else {
            // На сервере формируем URL из переданного пути или используем route
            const path = currentPath || (() => {
                try {
                    const route = useRoute()
                    return route.path
                } catch (e) {
                    return ''
                }
            })()
            
            if (path) {
                const protocol = process.env.NODE_ENV === 'production' ? 'https' : 'http'
                const host = process.env.NODE_ENV === 'production' ? 'swim4you.ru' : 'swim4you.local'
                url = `${protocol}://${host}${path}`
            }
        }
    }

    // Формируем тип контента
    const ogType = seoData.type || 'website'

    // Формируем название сайта
    const siteName = seoData.site_name || defaultSiteName

    // Формируем локаль
    const locale = seoData.locale || 'ru'

    // Формируем ogTitle
    const ogTitle = seoData.openGraphTitle || seoData.title || title || ''

    // Собираем все мета-теги в один объект для useHead
    const headTags: any = {
        title: title || undefined
    }

    // Основные мета-теги
    const metaTags: any[] = []

    if (description) {
        metaTags.push({
            name: 'description',
            content: description
        })
    }

    if (seoData.author) {
        metaTags.push({
            name: 'author',
            content: seoData.author
        })
    }

    if (seoData.robots) {
        metaTags.push({
            name: 'robots',
            content: seoData.robots
        })
    }

    // Open Graph мета-теги
    if (ogTitle) {
        metaTags.push({
            property: 'og:title',
            content: ogTitle
        })
    }

    if (description) {
        metaTags.push({
            property: 'og:description',
            content: description
        })
    }

    if (seoData.image) {
        metaTags.push({
            property: 'og:image',
            content: seoData.image
        })
    }

    metaTags.push({
        property: 'og:type',
        content: ogType
    })

    if (url) {
        metaTags.push({
            property: 'og:url',
            content: url
        })
    }

    metaTags.push({
        property: 'og:site_name',
        content: siteName
    })

    metaTags.push({
        property: 'og:locale',
        content: locale
    })

    // Twitter Card мета-теги
    metaTags.push({
        name: 'twitter:card',
        content: seoData.image ? 'summary_large_image' : 'summary'
    })

    if (ogTitle) {
        metaTags.push({
            name: 'twitter:title',
            content: ogTitle
        })
    }

    if (description) {
        metaTags.push({
            name: 'twitter:description',
            content: description
        })
    }

    if (seoData.image) {
        metaTags.push({
            name: 'twitter:image',
            content: seoData.image
        })
    }

    if (seoData.twitter_username) {
        metaTags.push({
            name: 'twitter:site',
            content: `@${seoData.twitter_username}`
        })
    }

    if (metaTags.length > 0) {
        headTags.meta = metaTags
    }

    // Canonical URL
    if (seoData.canonical_url) {
        headTags.link = [
            {
                rel: 'canonical',
                href: seoData.canonical_url
            }
        ]
    }

    // Favicon
    if (seoData.favicon) {
        if (!headTags.link) {
            headTags.link = []
        }
        headTags.link.push({
            rel: 'icon',
            href: seoData.favicon
        })
    }

    // Article мета-теги
    if (ogType === 'article') {
        const articleMeta: any[] = []
        
        if (seoData.published_time) {
            articleMeta.push({
                property: 'article:published_time',
                content: seoData.published_time
            })
        }
        
        if (seoData.modified_time) {
            articleMeta.push({
                property: 'article:modified_time',
                content: seoData.modified_time
            })
        }
        
        if (seoData.section) {
            articleMeta.push({
                property: 'article:section',
                content: seoData.section
            })
        }
        
        if (seoData.tags && seoData.tags.length > 0) {
            seoData.tags.forEach(tag => {
                articleMeta.push({
                    property: 'article:tag',
                    content: tag
                })
            })
        }

        if (articleMeta.length > 0) {
            if (!headTags.meta) {
                headTags.meta = []
            }
            headTags.meta.push(...articleMeta)
        }
    }

    // Schema.org разметка
    if (seoData.schema) {
        headTags.script = [
            {
                type: 'application/ld+json',
                children: JSON.stringify(seoData.schema)
            }
        ]
    }

    // Alternates (для мультиязычности)
    if (seoData.alternates) {
        if (!headTags.link) {
            headTags.link = []
        }
        if (Array.isArray(seoData.alternates)) {
            seoData.alternates.forEach((alternate: any) => {
                headTags.link.push({
                    rel: 'alternate',
                    hreflang: alternate.hreflang,
                    href: alternate.href
                })
            })
        }
    }

    // Возвращаем объект для useHead вместо вызова useHead
    return headTags
}

